import type { EcoaFormData, DemographicEntry } from "@porchpass/ui";
import type {
  BiologicalSexType,
  Ethnicity,
  EthnicityType,
  Race,
  RaceType,
  UpdateEthnicityFormData,
} from "src/types/ethnicity";

function toDemographicEntries(
  ethnicities?: Ethnicity[],
): DemographicEntry[] | undefined {
  if (!ethnicities?.length) return undefined;
  return ethnicities.map((e) => ({
    type: e.ethnicity_type ?? "",
    other: e.ethnicity_other,
  }));
}

function fromDemographicEntries(
  entries?: DemographicEntry[],
): Ethnicity[] | undefined {
  if (!entries?.length) return undefined;
  return entries.map((e) => ({
    ethnicity_type: e.type as EthnicityType,
    ethnicity_other: e.other,
  }));
}

function toRaceEntries(races?: Race[]): DemographicEntry[] | undefined {
  if (!races?.length) return undefined;
  return races.map((r) => ({
    type: r.race_type ?? "",
    other: r.race_other,
  }));
}

function fromRaceEntries(entries?: DemographicEntry[]): Race[] | undefined {
  if (!entries?.length) return undefined;
  return entries.map((e) => ({
    race_type: e.type as RaceType,
    race_other: e.other,
  }));
}

export function toEcoaEthnicityFormData(
  dto: UpdateEthnicityFormData,
): Pick<EcoaFormData, "ethnicities"> {
  return { ethnicities: toDemographicEntries(dto.ethnicities) };
}

export function fromEcoaEthnicityFormData(
  data: Pick<EcoaFormData, "ethnicities">,
): Pick<UpdateEthnicityFormData, "ethnicities"> {
  return { ethnicities: fromDemographicEntries(data.ethnicities) };
}

export function toEcoaRaceFormData(
  dto: UpdateEthnicityFormData,
): Pick<EcoaFormData, "races"> {
  return { races: toRaceEntries(dto.races) };
}

export function fromEcoaRaceFormData(
  data: Pick<EcoaFormData, "races">,
): Pick<UpdateEthnicityFormData, "races"> {
  return { races: fromRaceEntries(data.races) };
}

export function toEcoaSexFormData(
  dto: UpdateEthnicityFormData,
): Pick<EcoaFormData, "biologicalSex"> {
  const sex = dto.biological_sexes?.[0];
  return { biologicalSex: sex };
}

export function fromEcoaSexFormData(
  data: Pick<EcoaFormData, "biologicalSex">,
): Pick<UpdateEthnicityFormData, "biological_sexes"> {
  if (!data.biologicalSex) return { biological_sexes: [] };
  return {
    biological_sexes: [data.biologicalSex as BiologicalSexType],
  };
}
